import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import Engagements from './EngagementsTableItem';
import axios from "axios";
import { Config } from "../../config";
import LoadingSpinner from '../../components/LoadingSpinner';

// Memoize the EngagementsTableItem component
const MemoizedEngagements = memo(Engagements);

// Initialize the cache ref outside the component to ensure it's stable
const globalControlsCache = { current: {} };

function EngagementsTable({
  title,
  engagements,
  selectedItems,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange = null,
  onDeleteEngagement,
  showConfirmModal,
  setShowConfirmModal,
  handleDeleteClick,
  idsToDelete,
  onCreateNew,
  userRole,
  columns = 'default' // 'default' for GRC, 'offensive' for offensive engagements
}) {
  const [selectAll, setSelectAll] = useState(false);
  const [isCheck, setIsCheck] = useState([]);
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState({});
  const [subservices, setSubservices] = useState({});
  const [counters, setCounters] = useState([]);
  
  // Loading states - separate states for different phases
  const [isFetching, setIsFetching] = useState(true);   // Data fetching phase
  const [isPreparing, setIsPreparing] = useState(true); // Data preparation phase
  const [isRendering, setIsRendering] = useState(true); // Visual rendering phase
  
  // Use this for the final rendered table rows
  const [renderedList, setRenderedList] = useState(null);

  // Cache for reference data
  const [prevCustomers, setPrevCustomers] = useState({});
  const [prevSubservices, setPrevSubservices] = useState({});

  // Add these new states
  const [rowsToRender, setRowsToRender] = useState([]);
  const [isFullyRendered, setIsFullyRendered] = useState(false);
  const renderQueueRef = useRef([]);
  const renderTimerRef = useRef(null);

  // Define renderNextRow as a ref callback to avoid recreation on each render
  const renderNextRowRef = useRef(null);

  // Column configuration based on engagement type
  const getColumns = () => {
    if (columns === 'offensive') {
      return [
        { key: 'reference', label: 'Referencia', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'organization', label: 'Organización', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'type', label: 'Tipo', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'status', label: 'Estado', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'progress', label: 'Progreso', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'findings', label: 'Hallazgos', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'actions', label: '', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' }
      ];
    } else {
      // Default GRC columns
      return [
        { key: 'reference', label: 'Referencia', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'organization', label: 'Organización', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'framework', label: 'Marco', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'status', label: 'Estado', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'progress', label: 'Progreso', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'score', label: 'Puntuación', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' },
        { key: 'actions', label: '', className: 'px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap' }
      ];
    }
  };

  const tableColumns = getColumns();

  // First effect just for fetching reference data
  useEffect(() => {
    const fetchReferenceData = async () => {
      setIsFetching(true);
      
      try {
        // Fetch only once
        if (Object.keys(prevCustomers).length === 0) {
          const token = localStorage.getItem('token');
          const custResponse = await axios.get(Config.APIBaseURL + '/customers/', {
            headers: {'access_token': `Bearer ${token}`},
            withCredentials: true
          });
          
          const customerMap = custResponse.data.reduce((acc, customer) => {
            acc[customer.id] = [customer.name, customer.short_name, customer.logo];
            return acc;
          }, {});
          
          setCustomers(customerMap);
          setPrevCustomers(customerMap);
        } else {
          setCustomers(prevCustomers);
        }
        
        if (Object.keys(prevSubservices).length === 0) {
          const token = localStorage.getItem('token');
          const subResponse = await axios.get(Config.APIBaseURL + '/subservices/', {
            headers: {'access_token': `Bearer ${token}`},
            withCredentials: true
          });
          
          const subserviceMap = subResponse.data.reduce((acc, subservice) => {
            acc[subservice.id] = [subservice.name, subservice.short_name];
            return acc;
          }, {});
          
          setSubservices(subserviceMap);
          setPrevSubservices(subserviceMap);
        } else {
          setSubservices(prevSubservices);
        }
        
        const token = localStorage.getItem('token');
        const countersResponse = await axios.get(Config.APIBaseURL + '/counters', {
          headers: {'access_token': `Bearer ${token}`},
          withCredentials: true
        });
        
        if (countersResponse.status === 200) {
          setCounters(countersResponse.data);
        }
      } catch (error) {
        console.error('Error fetching reference data:', error);
      } finally {
        setIsFetching(false);
      }
    };
    
    fetchReferenceData();
  }, []);  // Only run once on component mount

  // Initialize the function in a useEffect that runs once
  useEffect(() => {
    renderNextRowRef.current = () => {
      if (renderQueueRef.current.length === 0) {
        setIsRendering(false);
        setIsFullyRendered(true);
        return;
      }
      
      // Render one row at a time
      const nextRow = renderQueueRef.current.shift();
      
      setRowsToRender(prevRows => [...prevRows, nextRow]);
      
      // Schedule next row render with a small delay
      renderTimerRef.current = setTimeout(() => {
        if (renderNextRowRef.current) {
          renderNextRowRef.current();
        }
      }, 50);
    };
  }, []);  // Empty dependency array = run once

  // Modify the pagination effect to use the stable function reference
  useEffect(() => {
    const preparePaginatedData = () => {
      // Force loading state first
      setIsPreparing(true);
      setIsRendering(true);
      setIsFullyRendered(false);
      setRowsToRender([]);
      
      // Clear any pending renders
      if (renderTimerRef.current) {
        clearTimeout(renderTimerRef.current);
      }
      
      // Immediate timeout to let React render loading state
      setTimeout(() => {
        try {
          if (engagements && engagements.length > 0) {
            setList(engagements);
            
            // Calculate pagination slice
            const start = (currentPage - 1) * itemsPerPage;
            const end = start + itemsPerPage;
            const paginatedItems = engagements.slice(start, end);
            
            // Store full list but don't render yet
            setRenderedList(paginatedItems);
            renderQueueRef.current = [...paginatedItems];
            
            // Only hide the data prep spinner
            setIsPreparing(false);
            
            // Start incremental rendering with the stable function
            if (renderNextRowRef.current) {
              renderNextRowRef.current();
            }
          } else {
            setList([]);
            setRenderedList([]);
            setRowsToRender([]);
            setIsPreparing(false);
            setIsRendering(false);
            setIsFullyRendered(true);
          }
        } catch (error) {
          console.error('Error preparing data:', error);
          setIsPreparing(false);
          setIsRendering(false);
          setIsFullyRendered(true);
        }
      }, 0);
    };
    
    preparePaginatedData();
    
    return () => {
      if (renderTimerRef.current) {
        clearTimeout(renderTimerRef.current);
      }
    };
  }, [engagements, currentPage, itemsPerPage]);

  // Handle selection
  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    if (!selectAll) {
      setIsCheck(list.map(li => li._id));
    } else {
      setIsCheck([]);
    }
  };

  const handleClick = e => {
    const { id, checked } = e.target;
    setSelectAll(false);
    if (checked) {
      setIsCheck([...isCheck, id]);
    } else {
      setIsCheck(isCheck.filter(item => item !== id));
    }
  };

  // Update selected items
  useEffect(() => {
    if (selectedItems) {
      selectedItems(isCheck);
    }
  }, [isCheck]);

  // Show a loading state if any of the loading flags are true
  const isLoading = isFetching || isPreparing || isRendering;
  
  // Get loading message based on current phase
  const getLoadingMessage = () => {
    if (isFetching) return "Loading data...";
    if (isPreparing) return "Preparando datos...";
    if (isRendering) return "Renderizando tabla...";
    return "Loading...";
  };

  return (
    <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative">
      <header className="px-5 py-4">
        <h2 className="font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
        {isLoading && (
          <div className="text-xs text-slate-500 mt-1">
            {getLoadingMessage()}
          </div>
        )}
      </header>
      <div>
        <div className="overflow-x-auto">
          <table className="table-auto w-full dark:text-slate-300">
            {/* Table header */}
            <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
                  <div className="flex items-center">
                    <label className="inline-flex">
                      <span className="sr-only">Select all</span>
                      <input className="form-checkbox" type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                    </label>
                  </div>
                </th>
                {tableColumns.map((column) => (
                  <th key={column.key} className={column.className}>
                    <div className="font-semibold text-left">{column.label}</div>
                  </th>
                ))}
              </tr>
            </thead>
            {/* Table body */}
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              {isFetching || isPreparing ? (
                <tr>
                  <td colSpan={tableColumns.length + 1} className="px-2 first:pl-5 last:pr-5 py-8">
                    <div className="flex items-center justify-center">
                      <LoadingSpinner />
                      <span className="ml-2 text-slate-400">
                        {getLoadingMessage()}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : !renderedList || renderedList.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length + 1}>
                    <p className="text-center py-4">
                      No data found.{" "}
                      <a 
                        href="#" 
                        className="text-indigo-500 hover:text-indigo-600" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onCreateNew();
                        }}
                      >
                        Create one to get started
                      </a>
                    </p>
                  </td>
                </tr>
              ) : (
                <>
                  {/* Render rows that have been processed */}
                  {rowsToRender.map(engagement => (
                    <MemoizedEngagements
                      key={engagement.id}
                      id={engagement.id}
                      type={subservices[engagement.subtype_id]}
                      framework={engagement.framework?.framework_cat || 'New Assessment'}
                      frameworkId={engagement.framework?.id}
                      title={engagement.title}
                      customer={customers[engagement.customer_id]}
                      owner={engagement.owner_name}
                      updatedAt={engagement.updated_at}
                      updatedBy={engagement.last_updated_by}
                      canUpdate={engagement.can_update}
                      start={new Date(engagement.start).toLocaleString()}
                      finish={new Date(engagement.finish).toLocaleString()}
                      comments={engagement.comments}
                      status={engagement.status}
                      files={engagement.files}
                      handleClick={handleClick}
                      onDeleteEngagement={onDeleteEngagement}
                      showConfirmModal={showConfirmModal}
                      setShowConfirmModal={setShowConfirmModal}
                      idsToDelete={idsToDelete}
                      isChecked={isCheck.includes(engagement._id)}
                      getCounters={() => counters}
                      controlsCache={globalControlsCache}
                      userRole={userRole}
                    />
                  ))}
                  
                  {/* Show loading indicator if still rendering */}
                  {isRendering && rowsToRender.length > 0 && (
                    <tr>
                      <td colSpan={tableColumns.length + 1} className="px-2 first:pl-5 last:pr-5 py-4 text-center">
                        <div className="inline-flex items-center">
                          <LoadingSpinner size="sm" />
                          <span className="ml-2 text-slate-400 text-xs">
                            Cargando más filas... ({rowsToRender.length} of {renderedList.length})
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default EngagementsTable;
